<template>
  <RouterLink v-if="to && !href && !target" :to="to" :class="classes" @click="handleClick">
    <SvgIcon v-if="props.icon" :icon="props.icon" :alt="props.label" />
    <span v-if="props.iconPos === 'center' || props.iconOnly == true" class="hide__text">{{ props.label }}</span>
    <template v-else>{{ props.label }}</template>
    <v-tooltip v-if="tooltipText" activator="parent" location="bottom" class="hidden-xs">{{ tooltipText }}</v-tooltip>
  </RouterLink>
  <a v-else-if="href || target" :href="href || to" :target="target ? '_blank' : undefined" :class="classes" @click="handleClick">
    <SvgIcon v-if="props.icon" :icon="props.icon" :alt="props.label" />
    <span v-if="props.iconPos === 'center' || props.iconOnly == true" class="hide__text">{{ props.label }}</span>
    <template v-else>{{ props.label }}</template>
    <v-tooltip v-if="tooltipText" activator="parent" location="bottom" class="hidden-xs">{{ tooltipText }}</v-tooltip>
  </a>
  <button v-else :disabled="isDisabled" :class="classes" @click="handleClick">
    <SvgIcon v-if="props.icon" :icon="props.icon" :alt="props.label" />
    <span v-if="props.iconPos === 'center' || props.iconOnly == true" class="hide__text">{{ props.label }}</span>
    <template v-else>{{ props.label }}</template>
    <v-tooltip v-if="tooltipText" activator="parent" location="bottom" class="hidden-xs">{{ tooltipText }}</v-tooltip>
  </button>
</template>

<script lang="ts" setup>
import { ref, computed, watch } from 'vue';
import SvgIcon from './SvgIcon.vue';

const props = withDefaults(
  defineProps<{
    label: string;
    disabled?: boolean;
    slim?: boolean;
    to?: string | undefined;
    href?: string | undefined;
    target?: boolean;
    icon?: string | undefined;
    iconPos?: 'left' | 'right' | 'start' | 'end' | 'center' | undefined;
    iconOnly?: boolean;
    tooltip?: string | boolean | undefined;
    version?: 'primary' | 'secondary' | 'highlight' | 'action' | 'grey' | 'white' | 'error' | 'primary--800' | 'transparent';
  }>(),
  { label: 'button', disabled: false, slim: false, iconPos: 'left' }
);

const isDisabled = ref(props.disabled);

// Watch for changes in props.disabled and update isDisabled accordingly
watch(
  () => props.disabled,
  (newVal) => {
    isDisabled.value = newVal;
  }
);

const handleClick = (event: Event) => {
  if ((props.to || props.href || props.target) && props.disabled) {
    event.preventDefault();
    return;
  }

  if (!props.disabled) {
    isDisabled.value = true;
    setTimeout(() => {
      isDisabled.value = props.disabled;
    }, 2000); // Disable for 2 seconds
  }
};

const classes = computed(() => ({
  button: true,
  lang_translate: true,
  button__slim: props.slim,
  button__icon: props.iconOnly,
  // Only add iconPos class if not iconOnly
  [`button__icon--${props.iconPos || 'left'}`]: !!props.icon && !props.iconOnly,
  // If iconOnly and no version, default to transparent
  [`button__${props.iconOnly && !props.version ? 'transparent' : props.version || 'primary'}`]: true,
  disabled: (props.to || props.href || props.target) && props.disabled,
}));

const tooltipText = computed(() => {
  if (typeof props.tooltip === 'string') {
    return props.tooltip;
  } else if (props.tooltip === true) {
    return props.label;
  }
  return '';
});
</script>

<style scoped lang="scss">
//

.button {
  display: flex;
  justify-content: center;
  align-items: center;
  position: relative;
  min-width: 280px;
  height: 50px !important;
  color: rgb(var(--v-theme-white));
  background-color: rgb(var(--v-theme-primary));
  font-family: 'brandon-grotesque', sans-serif;
  font-size: 14px;
  font-weight: 600;
  font-stretch: normal;
  font-style: normal;
  line-height: 1;
  letter-spacing: 2px;
  text-align: center;
  border-radius: 4px;
  margin: 0;
  text-transform: uppercase;
  cursor: pointer;
  text-decoration: none;
  transition: background-color 0.2s ease, border-color 0.2s ease, color 0.2s ease;

  .svg__icon {
    width: 20px;
    height: 20px;
    fill: currentColor;
    margin-right: 12px;
    overflow: hidden;
    pointer-events: none;
  }

  &:hover:not(:disabled) {
    background-color: rgb(var(--v-theme-primary-hover));
  }

  &:focus {
    border: 2px solid rgb(var(--v-theme-primary-focus));
  }
  &:disabled,
  &.disabled {
    opacity: 0.4;
    cursor: not-allowed;
  }

  &__slim {
    font-size: 12px;
    height: 40px !important;
    min-width: 220px;

    .svg__icon {
      width: 16px;
      height: 16px;
    }
  }

  &__secondary {
    color: rgb(var(--v-theme-secondary-text));
    background-color: rgb(var(--v-theme-secondary));
    &:hover:not(:disabled) {
      background-color: rgb(var(--v-theme-secondary-hover));
    }
    &:focus {
      border-color: rgb(var(--v-theme-secondary-focus));
    }
  }
  &__highlight {
    color: rgb(var(--v-theme-highlight-text));
    background-color: rgb(var(--v-theme-highlight));
    &:hover:not(:disabled) {
      background-color: rgb(var(--v-theme-highlight-hover));
    }
  }
  &__action {
    color: rgb(var(--v-theme-action-text));
    background-color: rgb(var(--v-theme-action));
    &:hover:not(:disabled) {
      background-color: rgb(var(--v-theme-action-hover));
    }
  }
  &__grey {
    color: rgb(var(--v-theme-grey-text));
    background-color: rgb(var(--v-theme-grey));
    &:hover:not(:disabled) {
      background-color: rgb(var(--v-theme-grey-hover));
    }
  }
  &__white {
    color: rgb(var(--v-theme-primary));
    background-color: rgb(var(--v-theme-white));
    &:hover:not(:disabled) {
      background-color: rgb(var(--v-theme-grey-hover));
    }
  }
  &__error {
    color: rgb(var(--v-theme-error-text));
    background-color: rgb(var(--v-theme-error));
    &:hover:not(:disabled) {
      background-color: rgba(var(--v-theme-error), 0.8);
    }
  }
  &__primary--800 {
    color: rgb(var(--v-theme-white));
    background-color: rgb(var(--v-theme-primary-800));
    &:hover:not(:disabled) {
      background-color: rgb(var(--v-theme-primary-800), 0.8);
    }
  }

  &__transparent {
    color: rgb(var(--v-theme-primary));
    background-color: transparent;
    // border: 2px solid rgb(var(--v-theme-primary));
    &:hover:not(:disabled) {
      background-color: transparent;
      color: rgba(var(--v-theme-primary-hover));
    }
    &:focus {
      border: none;
    }
  }

  &__icon {
    justify-content: center;
    align-items: center;
    padding: 0;
    min-width: 50px;

    .hide__text {
      display: none;
    }
    .svg__icon {
      margin: 0;
      width: 24px;
      height: 24px;
    }

    &.button__slim {
      min-width: 40px;
      &.button__transparent {
        height: 26px !important;
        width: 26px;
        min-width: 26px;
      }
    }
  }

  &__icon--left .svg__icon {
    margin-right: 12px;
    margin-left: 0;
  }
  &__icon--right {
    flex-direction: row-reverse;
    .svg__icon {
      margin-left: 12px;
      margin-right: 0;
    }
  }
  &__icon--start {
    justify-content: space-between;
    padding: 0 16px;
    .svg__icon {
      margin-right: 12px;
      margin-left: 0;
    }
  }
  &__icon--end {
    flex-direction: row-reverse;
    justify-content: space-between;
    padding: 0 16px;
    .svg__icon {
      margin-left: 12px;
      margin-right: 0;
    }
  }
  &__icon--center {
    font-size: 0;
    justify-content: center;
    .hide__text {
      display: none;
    }
    .svg__icon {
      margin-left: 12px;
      margin-right: 12px;
    }
  }
}

a.button {
  max-width: max-content;
}
</style>
